<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		SidepanelLayout,
		SidepanelTabs,
		SidepanelContent,
		ParameterCard,
	} from '@Shelly/shelly-design-system';
	import { devices } from 'src/stores/lists/devices';
	import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { sidePanelState, sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { _deepClone } from 'src/helpers/utilities';
	import { real_id } from 'src/helpers/interface_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import type { ITempSchemeObj } from 'src/types/TemperatureSchemeType';
	import { extractHexId } from 'src/components/TemperatureScheme/helpers/temp_scheme_to_ui';
	import {
		convertExtTempSensorValue,
		findDeviceSensorID,
		findExtTempSensor,
		handleThermostatTemps,
	} from 'src/components/TemperatureScheme/helpers/utils';
	import { handleSidePanelTabClick } from '../../sidepanel-tabs-helpers';
	import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import ThermostatWizard from 'src/components/TemperatureScheme/TempSchemeWizard/ThermostatWizard.svelte';
	import ThermostatMain from './Tabs/ThermostatMain.svelte';
	import ThermostatChart from './Tabs/ThermostatChart.svelte';
	import ThermostatDevices from './Tabs/ThermostatDevices.svelte';
	import ThermostatFavPositions from './Tabs/ThermostatFavPositions.svelte';
	import ThermostatInfo from './Tabs/ThermostatInfo.svelte';

	const THERMOSTAT_TAB_CONTENT_CONFIG = [
		ThermostatMain,
		ThermostatChart,
		ThermostatDevices,
		ThermostatFavPositions,
		ThermostatInfo,
	];

	let tabIndex = 0;
	let showAddEditModal = false;

	$: id = $sidePanelStateCombined?.id;
	$: thermostat = $temp_schemes[id] ? (_deepClone($temp_schemes[id]) as ITempSchemeObj) : undefined;

	$: name = thermostat?._meta?.name || '';
	$: type = thermostat?._meta?.type || '';

	$: actuators =
		typeof thermostat === 'object'
			? thermostat?.toheat?.blk.reduce((acc, curr) => {
					const dev_id = extractHexId(curr?.r || '');
					if (dev_id) {
						return [
							...acc,
							$devices[dev_id] || {
								id: dev_id,
								deleted: true,
							},
						];
					}
					return acc;
				}, [])
			: [];

	$: sensorDecId = typeof thermostat === 'object' ? thermostat?.tschem?.r.split(':')[1] : undefined;
	$: deviceSensorId = findDeviceSensorID(sensorDecId, thermostat);
	$: deviceSensor = $devices[deviceSensorId] || { id: deviceSensorId, deleted: true };

	$: statusStore = getDeviceStatus(deviceSensorId);
	$: deviceSensorIsOnline = $statusStore?.online;

	$: statusDeviceSensorStore = getDeviceStatus(deviceSensor ? deviceSensor.id : undefined);
	$: statusDeviceSensor = $statusDeviceSensorStore;
	$: settingsDeviceSensorStore = deviceSensor ? getDeviceSettings(deviceSensor.id) : undefined;
	$: settingsDeviceSensor = $settingsDeviceSensorStore;
	$: extSensorsStatuses = status_configurator(statusDeviceSensor, deviceSensor, settingsDeviceSensor);

	$: units = typeof thermostat === 'object' ? (thermostat?.isinfh ? 'F' : 'C') : undefined;
	$: thermostatDefTemp = handleThermostatTemps(thermostat?.deftemp, units, thermostat?.isinfh);
	$: extTempSensor = findExtTempSensor(extSensorsStatuses, thermostat, deviceSensor);
	$: extTempSensorValue = convertExtTempSensorValue(
		extTempSensor?.value || extTempSensor?.sensor?.value,
		units,
	);

	$: mainTabIcon = type === 'heat' ? 'fat fa-temperature-sun' : 'fat fa-temperature-snow';

	$: tabs = [
		{ icon: mainTabIcon, isActive: tabIndex === 0, disabled: false, id: 'main' },
		{ icon: 'fat fa-chart-line', isActive: tabIndex === 1, disabled: false, id: 'chart' },
		{ icon: 'fat fa-microchip', isActive: tabIndex === 2, disabled: false, id: 'devices' },
		{ icon: 'fak fa-light-sensor-on-gear', isActive: tabIndex === 3, disabled: false, id: 'fav' },
		{ icon: 'fat fa-circle-info', isActive: tabIndex === 4, disabled: false, id: 'info' },
	];

	const onTabClick = (e: CustomEvent) => {
		const selectedTabData = e.detail;
		tabIndex = selectedTabData.index;
		tabs = handleSidePanelTabClick(tabs, tabIndex);
	};

	const resetActiveTab = () => {
		tabIndex = 0;
	};

	const handleThermostatEdit = () => {
		thermostat = _deepClone($temp_schemes[id]) as ITempSchemeObj;
		showAddEditModal = true;
	};

	$: !$sidePanelState.open ? resetActiveTab() : null;
</script>

{#if typeof thermostat === 'object'}
	<SidepanelLayout
		title={name}
		settingsIcon={canManageHomeEntities($homes.list) ? 'fat fa-pen' : undefined}
		onSettings={canManageHomeEntities($homes.list) ? handleThermostatEdit : undefined}
		hideSettings={!canManageHomeEntities($homes.list)}
	>
		<svelte:component
			this={THERMOSTAT_TAB_CONTENT_CONFIG?.[tabIndex]}
			{id}
			{units}
			{thermostatDefTemp}
			{type}
			{actuators}
			{deviceSensorId}
			{deviceSensor}
			{deviceSensorIsOnline}
			{extTempSensor}
			{extTempSensorValue}
		/>

		<SidepanelTabs slot="tabs" {tabs} on:click={onTabClick} />
	</SidepanelLayout>
{:else if id !== '-1'}
	<SidepanelLayout title={$format('title_deleted_thermostat', { default: 'Deleted thermostat' })}>
		<SidepanelContent>
			<ParameterCard
				icon="fas fa-temperature-half"
				title={$format('label_not_found_el', { default: 'Not found element' })}
				description={'ID:' + id}
				size="m"
			/>
		</SidepanelContent>
	</SidepanelLayout>
{/if}

{#if showAddEditModal}
	<ThermostatWizard bind:id bind:showAddEditModal temp_scheme_object={thermostat} />
{/if}
